import { useEffect, useReducer, useRef, useState } from "react";
import type { MeterId } from "@zookeepbit/sim";
import type { GameViewProps, LookKind, MenuId } from "../ui/view-model";
import { browserApi, resolveBaseUrl } from "../account/api";
import { createCareCurves } from "./care-curves";
import { createBaseStore } from "../account/base-store";
import { createBridge, type Bridge } from "../account/bridge";
import { ANALYTICS_DEFAULT_ON, createAnalytics } from "./analytics";
import { parseDripWeeks } from "./debug-options";
import { createGame, type Game, type SaveStore } from "./game";
import { createLocalStore } from "./local-store";
import { browserEnv, startRuntime } from "./runtime";
import { createSound, soundForToast, type AudioLike, type Sound, type SoundPlayer } from "./sound";

/** Browser audio, made on first use; the Sound effects switch is read from the device save each time. */
function browserSound(read: () => boolean): SoundPlayer {
  return createSound({
    enabled: read,
    create: () => {
      const Ctor = (window as unknown as { AudioContext?: new () => AudioLike }).AudioContext;
      return Ctor ? new Ctor() : null;
    },
  });
}

/** Each care meter has its own sound. */
const CARE_SOUND: Record<MeterId, Sound> = { food: "care-food", rest: "care-rest", fun: "care-play", clean: "care-clean", love: "care-love" };

/** Debug tools are for local runs and the test site only, never on the public game. */
function debugEnabled(): boolean {
  const host = window.location.hostname;
  return import.meta.env.DEV || host === "localhost" || host === "127.0.0.1" || host.startsWith("test.");
}

function safeStorage(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

/** Hands text to the browser as a downloaded file. */
function saveFile(name: string, text: string): void {
  const url = URL.createObjectURL(new Blob([text], { type: "application/json" }));
  const link = document.createElement("a");
  link.href = url;
  link.download = name;
  link.click();
  URL.revokeObjectURL(url);
}

/** Anonymous usage counts: the switch lives on this device only (it is not part of the saved game). */
const USAGE_KEY = "zb-usage-counts";
function usageOn(): boolean {
  try {
    const stored = safeStorage()?.getItem(USAGE_KEY);
    return stored === null || stored === undefined ? ANALYTICS_DEFAULT_ON : stored === "1";
  } catch {
    return ANALYTICS_DEFAULT_ON;
  }
}
function setUsageOn(on: boolean): void {
  try {
    safeStorage()?.setItem(USAGE_KEY, on ? "1" : "0");
  } catch {
    /* private mode: the choice lasts until the page closes */
  }
}
/** Counts are only sent from the real game site, never from localhost or the test site. */
const usage = createAnalytics({
  enabled: () => !debugEnabled() && usageOn(),
  send: (events) => browserApi().sendEvents(events),
});

/** The owner's tuned care numbers: fetched in the background, the last answer kept on the device. */
const careCurves = createCareCurves({
  fetch: (url, init) => fetch(url, { ...init, credentials: "omit" }),
  storage: safeStorage(),
  baseUrl: resolveBaseUrl(import.meta.env.VITE_API_URL as string | undefined, window.location.hostname),
});

/** One game (and its account bridge) per page, created on first use (not during render, which the React lint rules rightly forbid). */
let instance: { game: Game; bridge: Bridge; sound: SoundPlayer } | null = null;
let onAccountChange: () => void = () => {};
function theGame(): { game: Game; bridge: Bridge; sound: SoundPlayer } {
  if (instance) return instance;
  usage.track("app_open");
  void careCurves.refresh();
  window.setInterval(() => void usage.flush(), 60_000);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") void usage.flush();
  });
  const local = createLocalStore(safeStorage());
  const holder: { bridge: Bridge | null; game: Game | null } = { bridge: null, game: null };
  const store: SaveStore = {
    load: () => local.load(),
    save(save) {
      local.save(save);
      holder.bridge?.localSaved();
    },
  };
  const game = createGame({
    clock: { now: () => Date.now() },
    store,
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    debug: debugEnabled(),
    careConfig: () => careCurves.get(),
    dripWeeks: parseDripWeeks(window.location.search, debugEnabled()),
  });
  const bridge = createBridge({
    api: browserApi(),
    device: { read: () => local.load(), adopt: (save) => holder.game?.loadSave(save) },
    saveFile,
    base: createBaseStore(safeStorage()),
    origin: window.location.origin,
    address: { search: window.location.search, pathname: window.location.pathname, hash: window.location.hash },
    replaceUrl: (url) => window.history.replaceState(null, "", url),
    navigate: (url) => window.location.assign(url),
    onOwned: (ids) => {
      holder.game?.setOwned(ids);
      onAccountChange();
    },
    offered: (id) => holder.game?.shopOffered(id) ?? false,
    onChange: () => onAccountChange(),
    seen: {
      read: () => {
        try {
          return safeStorage()?.getItem("zb-had-account") === "1";
        } catch {
          return false;
        }
      },
      mark: () => {
        try {
          safeStorage()?.setItem("zb-had-account", "1");
        } catch {
          /* private mode: the first-time hint just stays on */
        }
      },
    },
  });
  holder.game = game;
  holder.bridge = bridge;
  const sound = browserSound(() => local.load().settings.sound);
  instance = { game, bridge, sound };
  return instance;
}

/** The one hook the app uses: the props for the game screen, kept fresh by the runtime. */
export function useGame(): GameViewProps {
  const [, refresh] = useReducer((n: number) => n + 1, 0);
  const [{ game, bridge, sound }] = useState(theGame);
  const playedToast = useRef(0);

  useEffect(() => startRuntime(game, browserEnv(), refresh), [game]);
  useEffect(() => {
    onAccountChange = refresh;
    const env = browserEnv();
    const stop = bridge.run(env.onVisibilityChange, env.isVisible);
    return () => {
      stop();
      onAccountChange = () => {};
    };
  }, [bridge]);

  const view = game.view();
  // Each new message plays its sound once (toast ids only grow).
  useEffect(() => {
    for (const t of view.toasts) {
      if (t.id <= playedToast.current) continue;
      playedToast.current = t.id;
      const name = soundForToast(t.text);
      if (name) sound.play(name);
    }
  }, [view.toasts, sound]);
  const prefersReduced = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;
  return {
    ...view,
    reducedMotion: view.reducedMotion || prefersReduced,
    account: bridge.view(),
    accountActions: bridge.actions,
    friends: bridge.friendsView(),
    friendsActions: bridge.friendsActions,
    shop: bridge.shopView(),
    shopActions: bridge.shopActions,
    consent: bridge.consentView(),
    consentActions: bridge.consentActions,
    usageCounts: {
      on: usageOn(),
      onSet: (on: boolean) => {
        setUsageOn(on);
        refresh();
      },
    },
    onMenu: (id: MenuId) => {
      if (id === "book") usage.track("book_open");
      if (id === "items") usage.track("items_open");
      game.menu(id);
      refresh();
    },
    onCare: (meter: MeterId) => {
      sound.unlock();
      // A sound and a usage count only for a tap that did something: not for a meter that is still climbing, and not the lullaby when waking.
      if (game.care(meter) === "cared") {
        sound.play(CARE_SOUND[meter]);
        usage.track(`care_${meter}`);
      }
      refresh();
    },
    onHatch: (name: string) => {
      sound.unlock();
      sound.play("hatch");
      usage.track("hatch");
      game.hatch(name);
      refresh();
    },
    onCloseBook: () => {
      game.closeBook();
      refresh();
    },
    onVisit: (id: string) => {
      game.visit(id);
      refresh();
    },
    onLeaveVisit: () => {
      game.leaveVisit();
      refresh();
    },
    onSetting: (key, value) => {
      game.setSetting(key, value);
      refresh();
    },
    onRename: (name: string) => {
      game.rename(name);
      refresh();
    },
    onPickLook: (kind: LookKind, index: number) => {
      game.pickLook(kind, index);
      refresh();
    },
    onDismissToast: (id: number) => {
      game.dismissToast(id);
      refresh();
    },
    debug: game.debug.enabled
      ? {
          nowLabel: game.debug.nowLabel(),
          advance: (ms: number) => {
            game.debug.advance(ms);
            refresh();
          },
          reset: () => {
            game.debug.reset();
            refresh();
          },
        }
      : undefined,
  };
}
